function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || window.themeMoneyFormat || '${{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(number) || number == null) return 0;
    number = (number / 100.0).toFixed(precision);
    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsAmount = parts[1] ? (decimal + parts[1]) : '';
    return dollarsAmount + centsAmount;
  }

  const match = formatString.match(placeholderRegex);
  if (!match) return formatString;

  let value = '';
  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.getAddonInputs().forEach((input) => {
        input.addEventListener('change', this.updateBundledPrice.bind(this));
      });
      this.updateBundledPrice();
      document.addEventListener('variant:price-updated', this.updateBundledPrice.bind(this));
      this.pendingAddonItems = [];
    }

    getProductRoot() {
      return this.closest('.product__info-container') || document;
    }

    getAddonInputs() {
      return Array.from(this.getProductRoot().querySelectorAll('[data-product-addon]'));
    }

    getSelectedAddonSources() {
      const sources = [];
      this.getAddonInputs().forEach((input) => {
        if (input.tagName === 'SELECT') {
          const option = input.selectedOptions[0];
          if (option) sources.push(option);
          return;
        }
        if (input.checked) sources.push(input);
      });
      return sources;
    }

    getEnabledAddonItems(quantity, bundleId) {
      const parentTitle = this.form && this.form.dataset.productTitle;
      return this.getSelectedAddonSources()
        .filter((source) => source.dataset.addonAvailable === 'true' && source.dataset.addonId)
        .map((source) => {
          const item = {
            id: Number(source.dataset.addonId),
            quantity
          };
          const properties = {};
          if (parentTitle) properties['Added with'] = parentTitle;
          if (bundleId) {
            properties._bundle_id = bundleId;
            properties._bundle_parent = bundleId;
          }
          if (Object.keys(properties).length) item.properties = properties;
          return item;
        });
    }

    getLineItemProperties(formData) {
      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (!match || value instanceof File) return;
        if (String(value).trim() !== '') {
          properties[match[1]] = value;
        }
      });
      return properties;
    }

    hasFileUploads(formData) {
      let found = false;
      formData.forEach((value) => {
        if (value instanceof File && value.size > 0) found = true;
      });
      return found;
    }

    hasOversizedUpload(formData) {
      const maxBytes = 6 * 1024 * 1024;
      let oversized = false;
      formData.forEach((value) => {
        if (value instanceof File && value.size > maxBytes) oversized = true;
      });
      return oversized;
    }

    hasUnsupportedUpload(formData) {
      const allowed = ['png', 'svg', 'pdf', 'ai', 'eps'];
      let unsupported = false;
      formData.forEach((value) => {
        if (!(value instanceof File) || value.size === 0) return;
        const name = (value.name || '').toLowerCase();
        const extension = name.split('.').pop();
        if (allowed.indexOf(extension) === -1) unsupported = true;
      });
      return unsupported;
    }

    removeEmptyFiles(formData) {
      const emptyKeys = [];
      formData.forEach((value, key) => {
        if (value instanceof File && value.size === 0) emptyKeys.push(key);
      });
      emptyKeys.forEach((key) => formData.delete(key));
      return formData;
    }

    getExtraTitles() {
      return this.getSelectedAddonSources()
        .filter((source) => source.dataset.addonAvailable === 'true' && source.dataset.addonId)
        .map((source) => source.dataset.addonTitle)
        .filter((title) => title);
    }

    applyExtraTitles(properties, extraTitles) {
      if (extraTitles.length === 1) {
        properties.Extra = extraTitles[0];
      } else {
        extraTitles.forEach((title, index) => {
          properties[`Extra ${index + 1}`] = title;
        });
      }
      return properties;
    }

    updateBundledPrice() {
      const root = this.getProductRoot();
      const priceWrap = root.querySelector('[id^="price-"]');
      if (!priceWrap) return;
      const priceEl = priceWrap.querySelector('[data-base-price]');
      if (!priceEl) return;

      const base = parseInt(priceEl.dataset.basePrice, 10) || 0;
      const compareAt = parseInt(priceEl.dataset.compareAt || '0', 10) || 0;
      let extra = 0;
      this.getSelectedAddonSources().forEach((source) => {
        if (source.dataset.addonAvailable === 'true') {
          extra += parseInt(source.dataset.addonPrice || '0', 10) || 0;
        }
      });

      const format = priceWrap.dataset.moneyFormat || window.themeMoneyFormat;
      const total = base + extra;
      const compareTotal = compareAt > 0 ? compareAt + extra : 0;

      priceWrap.querySelectorAll('.price__regular .price-item--regular').forEach((el) => {
        el.textContent = formatMoney(total, format);
      });
      priceWrap.querySelectorAll('.price-item--sale').forEach((el) => {
        el.textContent = formatMoney(total, format);
      });
      if (compareTotal) {
        priceWrap.querySelectorAll('s.price-item--regular').forEach((el) => {
          el.textContent = formatMoney(compareTotal, format);
        });
      }
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();
      this.error = false;

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';

      const formData = this.removeEmptyFiles(new FormData(this.form));
      if (this.hasUnsupportedUpload(formData)) {
        this.handleErrorMessage('Use a PNG, SVG, PDF, AI, or EPS file.');
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        return;
      }
      if (this.hasOversizedUpload(formData)) {
        this.handleErrorMessage('This file is over 6 MB. Choose a file that is 6 MB or smaller.');
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        return;
      }
      const quantity = parseInt(formData.get('quantity') || '1', 10) || 1;
      const addonItemsPreview = this.getEnabledAddonItems(quantity);
      const hasFiles = this.hasFileUploads(formData);
      const extraTitles = this.getExtraTitles();
      this.pendingAddonItems = [];

      if (this.cart && typeof this.cart.setActiveElement === 'function') {
        this.cart.setActiveElement(document.activeElement);
      }

      const bundleId = addonItemsPreview.length
        ? ((window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : String(Date.now()))
        : '';

      if (addonItemsPreview.length && !hasFiles) {
        const properties = this.applyExtraTitles(this.getLineItemProperties(formData), extraTitles);
        properties._bundle_id = bundleId;
        const payload = {
          items: [
            { id: Number(formData.get('id')), quantity, properties },
            ...this.getEnabledAddonItems(quantity, bundleId)
          ]
        };
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
        }
        config.body = JSON.stringify(payload);
      } else {
        delete config.headers['Content-Type'];
        if (bundleId) {
          formData.append('properties[_bundle_id]', bundleId);
          if (extraTitles.length === 1) {
            formData.append('properties[Extra]', extraTitles[0]);
          } else {
            extraTitles.forEach((title, index) => {
              formData.append(`properties[Extra ${index + 1}]`, title);
            });
          }
        }
        if (this.cart && !hasFiles) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;
        this.pendingAddonItems = hasFiles && bundleId
          ? this.getEnabledAddonItems(quantity, bundleId)
          : [];
      }

      let uploadTimer;
      if (hasFiles && typeof AbortController !== 'undefined') {
        const controller = new AbortController();
        config.signal = controller.signal;
        uploadTimer = window.setTimeout(() => controller.abort(), 120000);
      }

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => this.readCartAddResponse(response))
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            const pendingAddons = this.pendingAddonItems || [];
            this.pendingAddonItems = [];
            if (!pendingAddons.length) {
              window.location = window.routes.cart_url;
              return;
            }
            return fetch(`${routes.cart_add_url}`, {
              ...fetchConfig('javascript'),
              headers: {
                ...fetchConfig('javascript').headers,
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({ items: pendingAddons })
            }).finally(() => {
              window.location = window.routes.cart_url;
            });
          }

          this.error = false;

          const addonItems = this.pendingAddonItems || [];
          this.pendingAddonItems = [];
          const addonPromise = addonItems.length
            ? fetch(`${routes.cart_add_url}`, {
                ...fetchConfig('javascript'),
                headers: {
                  ...fetchConfig('javascript').headers,
                  'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                  items: addonItems,
                  sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : undefined,
                  sections_url: this.cart ? window.location.pathname : undefined
                })
              }).then((addonRes) => addonRes.json()).then((addonResponse) => {
                if (addonResponse.status) return response;
                return addonResponse;
              })
            : Promise.resolve(response);

          return addonPromise.then((finalResponse) => {
            if (!finalResponse.key && finalResponse.items && finalResponse.items.length) {
              const parentItem = finalResponse.items.find((item) => {
                const properties = item.properties || {};
                return !properties._bundle_parent;
              });
              finalResponse.key = (parentItem || finalResponse.items[0]).key;
            }
            return this.ensureCartSections(finalResponse).then((cartResponse) => {
              const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
              const hasPopup = cartResponse.sections && sectionIds.some((id) => cartResponse.sections[id]);
              if (!hasPopup) {
                window.location = window.routes.cart_url;
                return;
              }
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener('modalClosed', () => {
                  setTimeout(() => { this.cart.renderContents(cartResponse) });
                }, { once: true });
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(cartResponse);
              }
            });
          });
        })
        .catch((error) => {
          console.error(error);
          const aborted = error && error.name === 'AbortError';
          this.handleErrorMessage(aborted
            ? 'The image is taking too long to upload. Please try again.'
            : (error && error.message) || 'Could not add this item to the cart. Please try again.');
        })
        .finally(() => {
          if (uploadTimer) window.clearTimeout(uploadTimer);
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    readCartAddResponse(response) {
      return response.text().then((text) => {
        const trimmed = (text || '').trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            return JSON.parse(trimmed);
          } catch (error) {
            // A non-JSON body is handled below.
          }
        }
        if (response.ok || response.redirected) {
          return { redirected: true };
        }
        throw new Error('Could not add this item to the cart. Please try again.');
      });
    }

    ensureCartSections(response) {
      if (!this.cart) return Promise.resolve(response);
      const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
      const hasSections = response.sections && sectionIds.every((id) => response.sections[id]);
      if (hasSections) return Promise.resolve(response);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        sections: sectionIds,
        sections_url: window.location.pathname
      });

      return fetch(routes.cart_update_url, config)
        .then((res) => res.json())
        .then((cartState) => {
          if (cartState && cartState.sections) {
            response.sections = Object.assign({}, response.sections || {}, cartState.sections);
          }
          if (!response.key && cartState && cartState.items && cartState.items.length) {
            const parentItem = cartState.items.find((item) => {
              const properties = item.properties || {};
              return !properties._bundle_parent;
            });
            response.key = (parentItem || cartState.items[0]).key;
          }
          return response;
        })
        .catch(() => response);
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}

function getCustomOptionMediaSource(root) {
  const select = root.querySelector('select');
  if (select) {
    return select.options[select.selectedIndex] || null;
  }
  return root.querySelector('input[type="radio"]:checked');
}

function storeOriginalImage(img) {
  if (!img || img.hasAttribute('data-original-src')) return;
  img.setAttribute('data-original-src', img.getAttribute('src') || '');
  img.setAttribute('data-original-srcset', img.getAttribute('srcset') || '');
}

function restoreOriginalImages(root) {
  if (!root) return;
  root.querySelectorAll('img[data-original-src]').forEach((img) => {
    const originalSrc = img.getAttribute('data-original-src');
    const originalSrcset = img.getAttribute('data-original-srcset');
    if (originalSrc) img.src = originalSrc;
    if (originalSrcset) {
      img.setAttribute('srcset', originalSrcset);
    } else {
      img.removeAttribute('srcset');
    }
  });
}

function setImageSource(img, url) {
  if (!img || !url) return;
  storeOriginalImage(img);
  img.removeAttribute('srcset');
  img.src = url;
  img.setAttribute('srcset', `${url} 2400w`);
  img.loading = 'eager';
}

function getRawMediaId(mediaId, sectionId) {
  if (!mediaId) return '';
  const prefix = `${sectionId}-`;
  return mediaId.indexOf(prefix) === 0 ? mediaId.slice(prefix.length) : mediaId;
}

function applyImagesIn(container, url) {
  if (!container || !url) return;
  container.querySelectorAll('img').forEach((img) => setImageSource(img, url));
}

function syncCustomOptionZoom(sectionId, mediaId, imageUrl) {
  const productModal = document.getElementById(`ProductModal-${sectionId}`);
  if (!productModal) return;

  const modalContent = productModal.querySelector('.product-media-modal__slider') || productModal.querySelector('.product-media-modal__content');
  if (!modalContent) return;

  const rawMediaId = getRawMediaId(mediaId, sectionId);
  let slide =
    (rawMediaId && typeof productModal.findSlideByMediaId === 'function' && productModal.findSlideByMediaId(rawMediaId)) ||
    (rawMediaId && modalContent.querySelector(`[data-media-id="${rawMediaId}"]`)) ||
    (mediaId && modalContent.querySelector(`[data-media-id="${mediaId}"]`));

  if (slide && slide.closest) {
    slide = slide.closest('.product-media-modal__slide') || slide;
  }

  if (!slide && productModal.slides && productModal.slides.length) {
    slide = productModal.querySelector('.product-media-modal__slide.active') || productModal.slides[0];
  }

  if (!slide) return;

  if (imageUrl) applyImagesIn(slide, imageUrl);

  modalContent.prepend(slide);

  if (typeof productModal.showActiveMedia === 'function' && productModal.slides) {
    productModal.slides.forEach((item) => item.classList.remove('active'));
    slide.classList.add('active');
    if (productModal.hasAttribute('open')) {
      productModal.scrollToSlide(slide, 'auto');
      productModal.updateNavButtons();
    }
  }

  const gallery = document.getElementById(`MediaGallery-${sectionId}`);
  const activeItem = gallery && gallery.querySelector('.product__media-item.is-active');
  if (activeItem && rawMediaId) {
    const opener = activeItem.querySelector('modal-opener, .product__media-toggle, [data-media-id]');
    if (opener) opener.setAttribute('data-media-id', rawMediaId);
    const toggle = activeItem.querySelector('.product__media-toggle');
    if (toggle) toggle.setAttribute('data-media-id', rawMediaId);
  }
}

function applyCustomOptionProductImage(root) {
  if (!root || !root.hasAttribute('data-update-product-image')) return;

  const source = getCustomOptionMediaSource(root);
  if (!source || !source.value) return;

  const sectionId = root.getAttribute('data-section');
  const mediaId = source.getAttribute('data-media-id');
  const imageUrl = source.getAttribute('data-image-url');
  const gallery = document.getElementById(`MediaGallery-${sectionId}`);
  if (!gallery) return;

  restoreOriginalImages(gallery);
  restoreOriginalImages(document.getElementById(`ProductModal-${sectionId}`));

  if (mediaId && typeof gallery.setActiveMedia === 'function') {
    try {
      gallery.setActiveMedia(mediaId, true);
    } catch (error) {
      console.error(error);
    }
    const activeItem = gallery.querySelector(`.product__media-item[data-media-id="${mediaId}"]`);
    if (activeItem && activeItem.classList.contains('is-active')) {
      syncCustomOptionZoom(sectionId, mediaId, null);
      return;
    }
  }

  if (!imageUrl) return;

  const activeItem =
    gallery.querySelector('.product__media-item.is-active') ||
    gallery.querySelector('.product__media-item');
  if (!activeItem) return;

  applyImagesIn(activeItem, imageUrl);
  syncCustomOptionZoom(sectionId, activeItem.getAttribute('data-media-id'), imageUrl);
}

document.addEventListener('change', (event) => {
  const root = event.target.closest('[data-update-product-image]');
  if (!root) return;
  applyCustomOptionProductImage(root);
});

document.addEventListener('click', (event) => {
  const root = event.target.closest('[data-update-product-image]');
  if (!root) return;

  const choice = event.target.closest('.custom-option__choice, .custom-option__swatch-wrap, label');
  if (!choice) return;

  const input = choice.matches('input[type="radio"]')
    ? choice
    : choice.querySelector('input[type="radio"]') || document.getElementById(choice.getAttribute('for') || '');

  if (input && input.type === 'radio' && !input.checked) {
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  requestAnimationFrame(() => applyCustomOptionProductImage(root));
});

function initCustomOptionProductImages() {
  document.querySelectorAll('[data-update-product-image]').forEach((root) => {
    applyCustomOptionProductImage(root);
  });
}

function swatchLabel(input) {
  return (input.getAttribute('data-swatch-name') || input.value || '').trim();
}

function updateSwatchSelected(root) {
  const selected = root.querySelector('[data-custom-swatch-selected]');
  if (!selected) return;
  const checked = root.querySelector('.product-form__input--custom-swatches input[type="radio"]:checked');
  selected.textContent = checked ? swatchLabel(checked) : '';
}

function swatchRow(viewport) {
  return viewport.querySelector('[data-custom-swatch-track]')
    || viewport.querySelector('.product-form__input--custom-swatches');
}

function swatchMetrics(viewport) {
  const row = swatchRow(viewport);
  const count = row ? row.querySelectorAll('.custom-option__swatch-wrap').length : 0;
  const pages = Math.max(1, Math.ceil(count / 8));
  return { pages };
}

function swatchPageOffset(viewport, index) {
  const row = swatchRow(viewport);
  const items = row ? row.querySelectorAll('.custom-option__swatch-wrap') : [];
  const pages = Math.max(1, Math.ceil(items.length / 8));
  const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  if (index >= pages - 1) return Math.round(max);
  const item = items[index * 8];
  if (!item) return 0;
  const left = item.getBoundingClientRect().left - viewport.getBoundingClientRect().left + viewport.scrollLeft;
  return Math.max(0, Math.min(Math.round(left), Math.round(max)));
}

function fitSwatchPage(rail) {
  const viewport = rail.querySelector('[data-custom-swatch-viewport]');
  const row = viewport && swatchRow(viewport);
  if (!viewport || !row) return;
  const items = row.querySelectorAll('.custom-option__swatch-wrap');
  const count = items.length;
  row.style.removeProperty('--swatch-size');
  if (count < 1) return;
  const styles = window.getComputedStyle(row);
  const gap = parseFloat(styles.columnGap || styles.gap) || 0;
  const size = items[0].getBoundingClientRect().width;
  if (size < 1) return;
  row.style.width = (count * size + Math.max(0, count - 1) * gap) + 'px';
  viewport.style.removeProperty('width');
  viewport.style.removeProperty('flex');
  viewport.style.maxWidth = 'calc(8 * 4.8rem + 7 * 0.4rem)';
}

function swatchPageState(viewport) {
  const metrics = swatchMetrics(viewport);
  const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  if (metrics.pages > 1 && viewport.scrollLeft >= max - 2) {
    return { metrics, index: metrics.pages - 1 };
  }
  let index = 0;
  let best = Infinity;
  for (let i = 0; i < metrics.pages; i += 1) {
    const distance = Math.abs(viewport.scrollLeft - swatchPageOffset(viewport, i));
    if (distance < best) {
      best = distance;
      index = i;
    }
  }
  return { metrics, index };
}

function syncSwatchPages(rail) {
  const viewport = rail.querySelector('[data-custom-swatch-viewport]');
  const prev = rail.querySelector('[data-custom-swatch-prev]');
  const next = rail.querySelector('[data-custom-swatch-next]');
  const dots = rail.querySelector('[data-custom-swatch-dots]');
  if (!viewport || !prev || !next) return;

  const { metrics, index } = swatchPageState(viewport);
  const canScroll = metrics.pages > 1;
  prev.hidden = !canScroll;
  next.hidden = !canScroll;
  prev.disabled = index <= 0;
  next.disabled = index >= metrics.pages - 1;

  if (!dots) return;
  dots.hidden = !canScroll;
  if (dots.childElementCount !== metrics.pages) {
    dots.replaceChildren();
    for (let i = 0; i < metrics.pages; i += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'custom-option__swatch-dot';
      button.setAttribute('aria-label', 'Color group ' + (i + 1));
      button.addEventListener('click', () => {
        viewport.scrollTo({ left: swatchPageOffset(viewport, i), behavior: 'smooth' });
      });
      dots.appendChild(button);
    }
  }
  Array.from(dots.children).forEach((button, i) => {
    const active = i === index;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-current', active ? 'true' : 'false');
  });
}

function scrollSwatches(rail, direction) {
  const viewport = rail.querySelector('[data-custom-swatch-viewport]');
  if (!viewport) return;
  const { metrics, index } = swatchPageState(viewport);
  const target = Math.min(metrics.pages - 1, Math.max(0, index + direction));
  viewport.scrollTo({ left: swatchPageOffset(viewport, target), behavior: 'smooth' });
}

function positionSwatchHover(rail, wrap) {
  const tip = rail.querySelector('[data-custom-swatch-hover]');
  const name = wrap.querySelector('.custom-option__swatch-name');
  if (!tip || !name) return;
  const label = name.textContent.trim();
  if (!label) {
    tip.hidden = true;
    return;
  }
  const railRect = rail.getBoundingClientRect();
  const wrapRect = wrap.getBoundingClientRect();
  tip.textContent = label;
  tip.hidden = false;
  tip.style.left = `${wrapRect.left - railRect.left + wrapRect.width / 2}px`;
  tip.style.top = `${wrapRect.top - railRect.top}px`;
}

function hideSwatchHover(rail) {
  const tip = rail.querySelector('[data-custom-swatch-hover]');
  if (tip) tip.hidden = true;
}

function initCustomSwatches(scope) {
  (scope || document).querySelectorAll('[data-custom-swatch-rail]').forEach((rail) => {
    if (rail.dataset.swatchReady === 'true') return;
    rail.dataset.swatchReady = 'true';
    const root = rail.closest('.custom-option');
    const viewport = rail.querySelector('[data-custom-swatch-viewport]');
    if (root) updateSwatchSelected(root);
    fitSwatchPage(rail);
    syncSwatchPages(rail);
    rail.querySelector('[data-custom-swatch-prev]')?.addEventListener('click', () => scrollSwatches(rail, -1));
    rail.querySelector('[data-custom-swatch-next]')?.addEventListener('click', () => scrollSwatches(rail, 1));
    viewport?.addEventListener('pointerdown', () => {
      viewport.dataset.lockScroll = String(viewport.scrollLeft);
      viewport.dataset.lockWinX = String(window.scrollX);
      viewport.dataset.lockWinY = String(window.scrollY);
    });
    viewport?.addEventListener('focusin', () => {
      if (viewport.dataset.lockScroll == null) return;
      const left = Number(viewport.dataset.lockScroll);
      const x = Number(viewport.dataset.lockWinX || 0);
      const y = Number(viewport.dataset.lockWinY || 0);
      const restore = () => {
        viewport.scrollLeft = left;
        if (Math.abs(window.scrollX - x) > 1 || Math.abs(window.scrollY - y) > 1) {
          window.scrollTo(x, y);
        }
      };
      restore();
      requestAnimationFrame(restore);
    });
    viewport?.addEventListener('scroll', () => syncSwatchPages(rail), { passive: true });
    window.addEventListener('resize', () => {
      fitSwatchPage(rail);
      syncSwatchPages(rail);
    });
    rail.addEventListener('mouseover', (event) => {
      const wrap = event.target.closest('.custom-option__swatch-wrap');
      if (!wrap || !rail.contains(wrap)) return;
      positionSwatchHover(rail, wrap);
    });
    rail.addEventListener('mouseout', (event) => {
      const wrap = event.target.closest('.custom-option__swatch-wrap');
      const next = event.relatedTarget && event.relatedTarget.closest
        ? event.relatedTarget.closest('.custom-option__swatch-wrap')
        : null;
      if (wrap && wrap !== next) hideSwatchHover(rail);
    });
    rail.addEventListener('focusin', (event) => {
      const wrap = event.target.closest('.custom-option__swatch-wrap');
      if (wrap) positionSwatchHover(rail, wrap);
    });
    rail.addEventListener('focusout', (event) => {
      if (!rail.contains(event.relatedTarget)) hideSwatchHover(rail);
    });
  });
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!input || input.type !== 'radio' || !input.hasAttribute('data-swatch-name')) return;
  const root = input.closest('.custom-option');
  if (root) updateSwatchSelected(root);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomOptionProductImages);
  document.addEventListener('DOMContentLoaded', () => initCustomSwatches());
} else {
  initCustomOptionProductImages();
  initCustomSwatches();
}

document.addEventListener('shopify:section:load', (event) => {
  initCustomSwatches(event.target);
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-custom-switcher]');
  if (!input) return;
  const root = input.closest('[data-custom-switcher-root]');
  const hidden = root && root.querySelector('[data-custom-switcher-value]');
  if (!hidden) return;
  hidden.value = input.checked ? input.getAttribute('data-on-name') : input.getAttribute('data-off-name');
});

function isDescInfoMobile(root) {
  const breakpoint = parseInt((root && root.getAttribute('data-mobile-breakpoint')) || '768', 10);
  return window.matchMedia('(max-width: ' + breakpoint + 'px)').matches;
}

function syncDescInfoToggle(root, expand) {
  const button = root.querySelector('[data-desc-info-toggle-btn]');
  const body = root.querySelector('.product-below-media__body');
  if (!button || !body) return;
  root.classList.toggle('is-expanded', expand);
  button.setAttribute('aria-expanded', String(expand));
  body.setAttribute('aria-hidden', String(!expand));
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-desc-info-toggle-btn]');
  if (!button) return;
  const root = button.closest('[data-desc-info-toggle]');
  if (!root) return;
  const expand = button.getAttribute('aria-expanded') !== 'true';
  syncDescInfoToggle(root, expand);
});

function initDescInfoToggle() {
  document.querySelectorAll('[data-desc-info-toggle]').forEach((root) => {
    syncDescInfoToggle(root, !isDescInfoMobile(root));
    root.setAttribute('data-ready', '');
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDescInfoToggle);
} else {
  initDescInfoToggle();
}

document.addEventListener('shopify:section:load', initDescInfoToggle);
